import { Icon } from "@circle-react-shared/Icon";
import Tippy from "@tippyjs/react";
import type { TippyProps } from "@tippyjs/react";
import { first } from "lodash";
import type { MouseEvent, ReactNode } from "react";
import { useState } from "react";
import { renderToString } from "react-dom/server";
import Flatpickr from "react-flatpickr";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import type { ButtonVariant } from "@/react/components/SettingsApp/CompassCompat/Button";

import "./styles.scss";

export interface DatePickerProps {
  appendTo?: TippyProps["appendTo"];
  cancelButtonText?: string;
  children?: ReactNode;
  className?: string;
  datePickerVisible?: boolean;
  enableTime?: boolean;
  minDate?: Date;
  onCancel: (event: MouseEvent<HTMLButtonElement>) => void;
  onClickOutside?: TippyProps["onClickOutside"];
  onSave: (date: string) => void;
  placement?: TippyProps["placement"];
  saveButtonVariant?: ButtonVariant;
  value: string;
}

export const DatePicker = ({
  cancelButtonText = t("quick_post.scheduled_post_ui.cancel"),
  children,
  className,
  datePickerVisible,
  enableTime = true,
  minDate = new Date(),
  onCancel,
  onClickOutside,
  onSave,
  placement = "top-end",
  saveButtonVariant = "primary",
  appendTo,
  value,
}: DatePickerProps) => {
  const [date, setDate] = useState<string>(value);

  return (
    <Tippy
      placement={placement}
      className={className}
      onClickOutside={onClickOutside}
      content={
        <div
          className="bg-primary border-primary rounded-md border shadow-xl"
          style={{
            maxWidth: "284px",
          }}
        >
          <Flatpickr
            options={{
              enableTime,
              dateFormat: enableTime ? "F d, Y G:i K" : "F d, Y",
              disableMobile: true,
              inline: true,
              altInput: true,
              altInputClass: "hidden",
              minDate,
              animate: false,
              prevArrow: renderToString(
                <Icon type="20-chevron-left" size={20} />,
              ),
              nextArrow: renderToString(
                <Icon type="20-chevron-right" size={20} />,
              ),
              minuteIncrement: enableTime ? 1 : undefined,
            }}
            value={value}
            onValueUpdate={dates => {
              if (first(dates)) {
                const selectedDate = dates[0];
                if (enableTime) {
                  setDate(selectedDate.toISOString());
                } else {
                  // For date-only selections, create a UTC date at noon to avoid timezone issues
                  const year = selectedDate.getFullYear();
                  const month = selectedDate.getMonth();
                  const day = selectedDate.getDate();
                  const utcDate = new Date(
                    Date.UTC(year, month, day, 12, 0, 0),
                  );
                  setDate(utcDate.toISOString());
                }
              }
            }}
          />
          <div className="border-primary grid grid-cols-2 gap-3 border-t px-5 py-3">
            <Button type="button" variant="secondary" onClick={onCancel} full>
              {cancelButtonText}
            </Button>
            <Button
              type="button"
              variant={saveButtonVariant}
              disabled={!date}
              onClick={event => {
                event?.preventDefault();
                onSave(date);
              }}
              full
            >
              {t("quick_post.scheduled_post_ui.save")}
            </Button>
          </div>
        </div>
      }
      visible={datePickerVisible}
      allowHTML
      interactive
      duration={0}
      arrow={false}
      delay={0}
      theme="modal-footer-actions"
      appendTo={appendTo || "parent"}
    >
      <div>{children}</div>
    </Tippy>
  );
};
